/**
 * Live ViewModels for Flight Engine V2 stages.
 * Maps existing booking records onto preview screen props.
 * No pricing math and no fabricated references.
 */

import { mapPaxListWithPassengerNames } from "../../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { formatFlightReviewDisplayMoney } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveFlightReviewDisplayMoney";
import { ceilLdsPaymentRemainingAmount } from "../../../utils/orders/ldsPaymentAmountRounding";
import {
  baggageExtraTitleHe,
  clampExtraQuantity,
  filterBaggageExtras,
  formatExtraUnitPrice,
  sliceDirectionLabelHe,
} from "../../unifiedFlightSearch/duffelExtras";
import { MOCK_PASSENGERS_FORM } from "../mock/flightEngineMockData";

export const LIVE_PASSPORT_REQUIRED_KEYS = ["passportNumber", "passportExpiry"];

function finiteNonNegative(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function positiveRate(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function currencyCode(value) {
  const c = String(value || "").trim().toUpperCase();
  if (c === "$" || c === "US$") return "USD";
  if (c === "₪" || c === "NIS") return "ILS";
  return c;
}

/**
 * Display labels only. USD comes from a source USD fare, otherwise ils / pricingFxRate.
 * No new FX source. Missing rate and missing USD → ILS label only.
 */
export function baggagePriceLabels({ ils, usdSource, sourceCurrency, fxRate } = {}) {
  const rawIls = finiteNonNegative(ils);
  // Same whole-shekel ceil as the order-summary cost. Once, on the ILS line only.
  const ilsAmount =
    rawIls != null && rawIls > 0 ? ceilLdsPaymentRemainingAmount(rawIls) : rawIls;
  const ilsLabel =
    ilsAmount != null ? formatFlightReviewDisplayMoney(ilsAmount, "ILS") || "" : "";
  const rate = positiveRate(fxRate);
  const source = finiteNonNegative(usdSource);
  const usdAmount =
    currencyCode(sourceCurrency) === "USD" && source != null
      ? source
      : rawIls != null && rate != null
        ? rawIls / rate
        : null;
  const usdLabel =
    usdAmount != null ? formatFlightReviewDisplayMoney(usdAmount, "USD") || "" : "";
  const both = Boolean(usdLabel && ilsLabel);
  return {
    priceLabel: ilsLabel || usdLabel,
    priceUsdLabel: both ? usdLabel : "",
    priceIlsLabel: both ? ilsLabel : "",
  };
}

const PASSENGER_VALUE_KEYS = [
  "firstName",
  "middleName",
  "lastName",
  "gender",
  "dateOfBirth",
  "nationality",
  "passportNumber",
  "passportExpiry",
  "issuingCountry",
  "passportIssueDate",
];

export function passengerSlotId(index) {
  return `pax-${index + 1}`;
}

function typeLabelForRole(role) {
  if (role === "infant") return "תינוק";
  if (role === "child") return "ילד";
  return "מבוגר";
}

export function bookingPassengersToScreenPassengers(passengers, roomInfo) {
  const list = Array.isArray(passengers) ? passengers : [];
  const named = mapPaxListWithPassengerNames({ roomInfo, passengers: list });
  return list.map((p, index) => {
    const values = {};
    for (const key of PASSENGER_VALUE_KEYS) {
      values[key] = p?.[key] == null ? "" : String(p[key]);
    }
    return {
      id: passengerSlotId(index),
      indexLabel: `נוסע ${index + 1}`,
      typeLabel: typeLabelForRole(named[index]?.role),
      values,
    };
  });
}

export function bookingContactFromPassengers(passengers) {
  const p = Array.isArray(passengers) ? passengers[0] || {} : {};
  const composed = [p.firstName, p.lastName].filter((part) => String(part || "").trim()).join(" ");
  return {
    name: String(p.contactName || composed || ""),
    phone: String(p.cell || ""),
    email: String(p.email || ""),
  };
}

export function buildLivePassengersFormModel(passengers, roomInfo) {
  const contact = bookingContactFromPassengers(passengers);
  const screenPassengers = bookingPassengersToScreenPassengers(passengers, roomInfo);
  const nationalityOptions = [...MOCK_PASSENGERS_FORM.nationalityOptions];
  const known = new Set(nationalityOptions.map((opt) => opt.value));
  for (const pax of screenPassengers) {
    const code = String(pax.values.nationality || "").trim();
    if (code && !known.has(code)) {
      nationalityOptions.push({ value: code, label: code });
      known.add(code);
    }
  }
  return {
    ...MOCK_PASSENGERS_FORM,
    nationalityOptions,
    contact: {
      ...MOCK_PASSENGERS_FORM.contact,
      values: contact,
    },
    passengers: screenPassengers,
  };
}

export function patchBookingPassengerField(passengers, id, key, value) {
  const index = Number(String(id || "").replace(/^pax-/, "")) - 1;
  const list = Array.isArray(passengers) ? passengers : [];
  if (!Number.isInteger(index) || index < 0 || index >= list.length) return list;
  if (!PASSENGER_VALUE_KEYS.includes(key)) return list;
  return list.map((p, i) => (i === index ? { ...p, [key]: value } : p));
}

export function patchBookingContact(passengers, key, value) {
  const field =
    key === "phone" ? "cell" : key === "email" ? "email" : key === "name" ? "contactName" : null;
  const list = Array.isArray(passengers) ? passengers : [];
  if (!field || list.length === 0) return list;
  return list.map((p, i) => (i === 0 ? { ...p, [field]: value } : p));
}

export function applyLdsSupplementToggle(prev, supplementKey, paxId, checked) {
  const extras = prev && typeof prev === "object" ? prev : {};
  const cur = new Set(extras.supplementPaxSelections?.[supplementKey] || []);
  if (checked) cur.add(paxId);
  else cur.delete(paxId);
  const nextArr = Array.from(cur);
  const supplementPaxSelections = { ...(extras.supplementPaxSelections || {}) };
  if (nextArr.length === 0) delete supplementPaxSelections[supplementKey];
  else supplementPaxSelections[supplementKey] = nextArr;
  return { ...extras, supplementPaxSelections };
}

export function applyDuffelServiceToggle(prev, extra, passengerId, checked, directionId = null) {
  const extras = prev && typeof prev === "object" ? prev : {};
  const duffelServiceSelections = { ...(extras.duffelServiceSelections || {}) };
  const id = String(extra?.serviceId || "");
  if (!id) return extras;
  if (!directionId) {
    if (!checked) {
      delete duffelServiceSelections[id];
    } else {
      const quantity = clampExtraQuantity(extra, 1);
      if (quantity <= 0) return extras;
      duffelServiceSelections[id] = {
        quantity,
        passengerId: passengerId || null,
      };
    }
    return { ...extras, duffelServiceSelections };
  }

  const current =
    duffelServiceSelections[id] && typeof duffelServiceSelections[id] === "object"
      ? duffelServiceSelections[id]
      : {};
  const byDirection = { ...(current.byDirection || {}) };
  const dir = String(directionId);
  const set = new Set(Array.isArray(byDirection[dir]) ? byDirection[dir] : []);
  const pid = String(passengerId || "");
  if (!pid) return extras;
  if (checked) set.add(pid);
  else set.delete(pid);
  if (set.size === 0) delete byDirection[dir];
  else byDirection[dir] = Array.from(set);

  const passengers = new Set();
  for (const ids of Object.values(byDirection)) {
    if (!Array.isArray(ids)) continue;
    for (const paxId of ids) passengers.add(paxId);
  }
  if (passengers.size === 0) {
    delete duffelServiceSelections[id];
  } else {
    const quantity = clampExtraQuantity(extra, passengers.size);
    if (quantity <= 0) delete duffelServiceSelections[id];
    else {
      duffelServiceSelections[id] = {
        quantity,
        passengerId: Array.from(passengers)[0] || null,
        byDirection,
      };
    }
  }
  return { ...extras, duffelServiceSelections };
}

/**
 * Composite extras identity. Same extraId may exist on outbound and inbound.
 * @param {string} passengerId
 * @param {string} directionId outbound | inbound | whole
 * @param {string} extraId supplementKey or serviceId
 */
export function baggageSelectionKey(passengerId, directionId, extraId) {
  return `${passengerId}::${directionId}::${extraId}`;
}

export function baggageOptionId(directionId, extraId) {
  return `${directionId}::${extraId}`;
}

export function directionIdForSegment(segmentIndex, segmentCount) {
  const count = Math.max(1, Number(segmentCount) || 1);
  if (count <= 1) return "whole";
  const index = Number(segmentIndex);
  if (index === 0) return "outbound";
  if (index === 1) return "inbound";
  return `seg-${Number.isFinite(index) ? index : 0}`;
}

function displayDate(value) {
  const s = String(value || "").trim();
  if (!s || s === "—") return "";
  return s;
}

function routeLabelFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  const from = String(leg.depIata || leg.origin || leg.originCode || "").trim();
  const to = String(leg.arrIata || leg.destination || leg.destinationCode || "").trim();
  if (!from && !to) return "";
  return [from, to].filter(Boolean).join(" → ");
}

function legForDirection(flight, directionId) {
  if (!flight || typeof flight !== "object") return null;
  if (directionId === "inbound") return flight.inbound || null;
  return flight.outbound || null;
}

function directionLabel(directionId, segmentCount, index) {
  if (directionId === "whole") return "כל ההזמנה";
  if (directionId === "outbound") return "הלוך";
  if (directionId === "inbound") return "חזור";
  return sliceDirectionLabelHe(index, segmentCount);
}

function buildDirections(segmentCount, flight) {
  const count = Math.max(1, Number(segmentCount) || 1);
  return Array.from({ length: count }, (_, index) => {
    const id = directionIdForSegment(index, count);
    const leg = legForDirection(flight, id);
    return {
      id,
      label: directionLabel(id, count, index),
      routeLabel: routeLabelFromLeg(leg),
      dateLabel: displayDate(leg?.date || leg?.dateLabel),
    };
  });
}

/**
 * Airports and dates from the selected flight ViewModel.
 * Search dates fill only when the leg ViewModel has no date.
 */
export function flightLegsForExtras(offer, search) {
  const outbound = offer?.outbound || null;
  const inbound = offer?.inbound || null;
  return {
    outbound: outbound
      ? {
          ...outbound,
          date: displayDate(outbound.date) || displayDate(search?.departDateLabel),
        }
      : null,
    inbound: inbound
      ? {
          ...inbound,
          date: displayDate(inbound.date) || displayDate(search?.returnDateLabel),
        }
      : null,
  };
}

function unknownBaggageOption() {
  return {
    id: "baggage-unknown",
    name: "פרטי כבודה לא זמינים",
    state: "unavailable",
    weightLabel: "",
    description: "",
    priceLabel: "",
    priceUsd: null,
  };
}

function explicitNoneOption() {
  return {
    id: "baggage-none",
    name: "ללא כבודה",
    state: "unavailable",
    weightLabel: "",
    description: "",
    priceLabel: "",
    priceUsd: null,
  };
}

/**
 * @param {{
 *   roomInfo?: object,
 *   passengers?: object[],
 *   ldsRows?: object[],
 *   duffelExtras?: object[],
 *   extras?: object,
 *   baggageLines?: string[],
 *   fare?: { family?: string|null, cabin?: string|null }|null,
 *   flight?: { outbound?: object|null, inbound?: object|null }|null,
 *   pricingFxRate?: number|null,
 * }} input
 */
export function buildLiveExtrasCatalog(input = {}) {
  const passengers = Array.isArray(input.passengers) ? input.passengers : [];
  const ldsPax = mapPaxListWithPassengerNames({
    roomInfo: input.roomInfo,
    passengers,
  });
  const lines = Array.isArray(input.baggageLines) ? input.baggageLines : [];
  const explicitNone = lines.includes("ללא כבודה");
  const ldsRows = (Array.isArray(input.ldsRows) ? input.ldsRows : []).filter(
    (row) => Number(row?.fareAmount) > 0
  );
  const duffelBags = filterBaggageExtras(input.duffelExtras || []);
  const duffelOther = (Array.isArray(input.duffelExtras) ? input.duffelExtras : []).filter(
    (extra) => extra?.serviceId && String(extra.type || "").toLowerCase() !== "baggage"
  );

  const useDuffel = duffelBags.length > 0 || (ldsRows.length === 0 && duffelOther.length > 0);
  let paxSource = useDuffel
    ? passengers
        .map((p, index) => {
          const id = String(p?.duffelPassengerId || p?.passengerId || "").trim();
          if (!id) return null;
          const name =
            [p.firstName, p.lastName].filter((part) => String(part || "").trim()).join(" ") ||
            `נוסע ${index + 1}`;
          return { id, name, typeLabel: typeLabelForRole(ldsPax[index]?.role) };
        })
        .filter(Boolean)
    : ldsPax.map((p, index) => ({
        id: p.id,
        name:
          [passengers[index]?.firstName, passengers[index]?.lastName]
            .filter((part) => String(part || "").trim())
            .join(" ") || p.label || `נוסע ${index + 1}`,
        typeLabel: typeLabelForRole(p.role),
      }));
  if (paxSource.length === 0) {
    paxSource = [
      {
        id: "pax-unassigned",
        name: "נוסע",
        typeLabel: "מבוגר",
      },
    ];
  }

  const segmentCount = useDuffel
    ? Math.max(
        1,
        ...duffelBags.flatMap((extra) =>
          Array.isArray(extra.sliceIndices) && extra.sliceIndices.length
            ? extra.sliceIndices.map((n) => Number(n) + 1)
            : [1]
        )
      )
    : Math.max(1, ...ldsRows.map((row) => Number(row.segmentIndex) + 1 || 1));
  const directions = buildDirections(segmentCount, input.flight);
  const directionMode = segmentCount > 1 ? "per-segment" : "whole-trip";

  /** @type {object[]} */
  let baggageOptions = [];
  if (!useDuffel && ldsRows.length) {
    baggageOptions = ldsRows.map((row) => {
      const ils = Number(input.extras?.supplementUnitFaresIls?.[row.supplementKey]);
      const storedSource = Number(input.extras?.supplementUnitFares?.[row.supplementKey]);
      const sourceAmount = Number.isFinite(storedSource) ? storedSource : Number(row.fareAmount);
      const labels = baggagePriceLabels({
        ils: Number.isFinite(ils) ? ils : null,
        usdSource: sourceAmount,
        sourceCurrency: row.currency,
        fxRate: input.pricingFxRate,
      });
      const directionId = directionIdForSegment(row.segmentIndex, segmentCount);
      const extraId = String(row.supplementKey);
      return {
        id: baggageOptionId(directionId, extraId),
        extraId,
        directionId,
        mutuallyExclusive: row.mutuallyExclusive === true,
        exclusiveGroup: row.exclusiveGroup ? String(row.exclusiveGroup) : null,
        name: row.titleLine || "כבודה",
        state: "available",
        weightLabel: row.weightLabel || "",
        description: row.description || "",
        priceLabel: labels.priceLabel,
        priceUsdLabel: labels.priceUsdLabel,
        priceIlsLabel: labels.priceIlsLabel,
        priceUsd: null,
        kind: "lds",
        supplementKey: extraId,
        segmentIndex: row.segmentIndex,
      };
    });
  } else if (useDuffel && (duffelBags.length || duffelOther.length)) {
    baggageOptions = expandDuffelBaggageOptions(duffelBags, segmentCount);
  } else {
    baggageOptions = [explicitNone ? explicitNoneOption() : unknownBaggageOption()];
  }

  const selections = {};
  const selectionKeys = [];
  for (const pax of paxSource) {
    selections[pax.id] = {};
    for (const dir of directions) selections[pax.id][dir.id] = [];
  }

  if (!useDuffel) {
    const chosen = input.extras?.supplementPaxSelections || {};
    for (const opt of baggageOptions) {
      if (opt.kind !== "lds") continue;
      const ids = chosen[opt.supplementKey] || [];
      for (const paxId of ids) {
        rememberDirectionSelection(selections, selectionKeys, paxId, opt);
      }
    }
  } else {
    const chosen = input.extras?.duffelServiceSelections || {};
    for (const opt of baggageOptions) {
      if (opt.kind !== "duffel") continue;
      const row = chosen[opt.serviceId];
      if (!row || Number(row.quantity) <= 0) continue;
      if (row.byDirection && typeof row.byDirection === "object") {
        const ids = Array.isArray(row.byDirection[opt.directionId])
          ? row.byDirection[opt.directionId]
          : [];
        for (const paxId of ids) {
          rememberDirectionSelection(selections, selectionKeys, paxId, opt);
        }
        continue;
      }
      const paxId = String(row.passengerId || "");
      const slices = Array.isArray(opt.sliceIndices) ? opt.sliceIndices : [];
      const primary = slices.length ? Number(slices[0]) : Number(opt.segmentIndex) || 0;
      if (Number(opt.segmentIndex) !== primary) continue;
      rememberDirectionSelection(selections, selectionKeys, paxId, opt);
    }
  }

  const inclusions = lines
    .filter((label) => label && label !== "פרטי כבודה לא זמינים" && label !== "ללא כבודה")
    .map((label, index) => ({
      id: `incl-${index}`,
      label,
      tone: "included",
    }));

  return {
    title: "תוספות וכבודה",
    subtitle: "",
    inclusionsTitle: "כלול במחיר",
    inclusions,
    fare: input.fare?.family
      ? { family: input.fare.family, cabin: input.fare.cabin || "" }
      : null,
    passengers: paxSource,
    directionMode,
    directions,
    baggageOptions,
    otherExtrasTitle: "תוספות נוספות",
    otherExtras: duffelOther.map((extra) => ({
      id: String(extra.serviceId),
      name: baggageExtraTitleHe(extra),
      description: "",
      priceLabel: formatExtraUnitPrice(extra.totalAmount, extra.totalCurrency),
      kind: "duffel",
      serviceId: String(extra.serviceId),
    })),
    selections,
    selectionKeys,
  };
}

function expandDuffelBaggageOptions(duffelBags, segmentCount) {
  const options = [];
  for (const extra of duffelBags) {
    const indices =
      Array.isArray(extra.sliceIndices) && extra.sliceIndices.length
        ? extra.sliceIndices
        : [0];
    const seen = new Set();
    for (const raw of indices) {
      const slice = Number(raw);
      if (!Number.isFinite(slice) || seen.has(slice)) continue;
      seen.add(slice);
      const directionId = directionIdForSegment(slice, segmentCount);
      const extraId = String(extra.serviceId);
      options.push({
        id: baggageOptionId(directionId, extraId),
        extraId,
        directionId,
        segmentIndex: slice,
        sliceIndices: Array.isArray(extra.sliceIndices) ? extra.sliceIndices.map(Number) : [],
        name: baggageExtraTitleHe(extra),
        state: "available",
        weightLabel: "",
        description: "",
        priceLabel: formatExtraUnitPrice(extra.totalAmount, extra.totalCurrency),
        priceUsd: null,
        kind: "duffel",
        serviceId: extraId,
        maximumQuantity: extra.maximumQuantity,
        mutuallyExclusive: extra.mutuallyExclusive === true,
        exclusiveGroup: extra.exclusiveGroup ? String(extra.exclusiveGroup) : null,
      });
    }
  }
  return options;
}

function findCatalogOption(catalog, optionId) {
  const bags = catalog?.baggageOptions || [];
  const others = catalog?.otherExtras || [];
  return (
    bags.find((row) => row.id === optionId) ||
    others.find((row) => row.id === optionId) ||
    null
  );
}

function directionSelectionIds(slot) {
  if (Array.isArray(slot)) return slot;
  if (slot) return [slot];
  return [];
}

function rememberDirectionSelection(selections, selectionKeys, paxId, opt) {
  if (!selections[paxId] || !opt?.directionId || !opt?.id) return;
  const list = selections[paxId][opt.directionId];
  if (!Array.isArray(list)) return;
  if (!list.includes(opt.id)) list.push(opt.id);
  const key = baggageSelectionKey(paxId, opt.directionId, opt.extraId);
  if (!selectionKeys.includes(key)) selectionKeys.push(key);
}

function sameExclusiveGroup(a, b) {
  if (a?.mutuallyExclusive !== true || b?.mutuallyExclusive !== true) return false;
  if (a.exclusiveGroup || b.exclusiveGroup) return a.exclusiveGroup === b.exclusiveGroup;
  return true;
}

function dropExclusivePeers(extras, catalog, opt, passengerId, duffelExtras) {
  if (opt?.mutuallyExclusive !== true) return extras;
  let next = extras;
  for (const other of catalog?.baggageOptions || []) {
    if (!other || other.id === opt.id || other.directionId !== opt.directionId) continue;
    if (!sameExclusiveGroup(opt, other)) continue;
    if (other.kind === "lds") {
      const ids = next.supplementPaxSelections?.[other.supplementKey] || [];
      if (Array.isArray(ids) && ids.includes(passengerId)) {
        next = applyLdsSupplementToggle(next, other.supplementKey, passengerId, false);
      }
    } else if (other.kind === "duffel") {
      const row = next.duffelServiceSelections?.[other.serviceId];
      const ids = row?.byDirection?.[other.directionId] || [];
      const selectedHere =
        (Array.isArray(ids) && ids.includes(passengerId)) ||
        (!row?.byDirection && String(row?.passengerId || "") === passengerId);
      if (!selectedHere) continue;
      const extra =
        (Array.isArray(duffelExtras) ? duffelExtras : []).find(
          (item) => String(item?.serviceId) === String(other.serviceId)
        ) || { serviceId: other.serviceId, maximumQuantity: other.maximumQuantity ?? 1 };
      next = applyDuffelServiceToggle(next, extra, passengerId, false, other.directionId || null);
    }
  }
  return next;
}

function baggageIdentityKey(option) {
  const name = String(option?.name || "").replace(/\s+/g, " ").trim().toLowerCase();
  const weight = String(option?.weightLabel || "").replace(/\s+/g, " ").trim().toLowerCase();
  return `${name}\u0000${weight}`;
}

/**
 * Return-direction option that is the same bag as an outbound option.
 * Same Duffel extra id wins; otherwise the same title and weight.
 * @param {object|null} option
 * @param {object[]} baggageOptions
 */
export function findMatchingReturnBaggageOption(option, baggageOptions) {
  if (!option || option.directionId !== "outbound") return null;
  const inbound = (Array.isArray(baggageOptions) ? baggageOptions : []).filter(
    (row) =>
      row &&
      row.directionId === "inbound" &&
      row.id !== option.id &&
      row.state !== "included" &&
      row.state !== "unavailable"
  );
  const extraId = String(option.extraId || "");
  if (extraId) {
    const sameExtra = inbound.find((row) => String(row.extraId || "") === extraId);
    if (sameExtra) return sameExtra;
  }
  const key = baggageIdentityKey(option);
  if (!key || key === "\u0000") return null;
  return inbound.find((row) => baggageIdentityKey(row) === key) || null;
}

/**
 * Add or remove one passenger + direction + extra through the existing extras object.
 * A passenger+direction holds every selected extra unless that option is explicitly mutuallyExclusive.
 */
export function toggleLiveBaggageSelection(prev, catalog, action = {}) {
  const extras = prev && typeof prev === "object" ? prev : {};
  const passengerId = String(action.passengerId || "");
  const optionId = action.optionId;
  const selected = Boolean(action.selected);
  const opt = findCatalogOption(catalog, optionId);
  if (!opt || !passengerId) return extras;
  if (opt.directionId && action.directionId && opt.directionId !== action.directionId) {
    return extras;
  }
  if (opt.kind === "lds") {
    if (!selected) return applyLdsSupplementToggle(extras, opt.supplementKey, passengerId, false);
    const next = dropExclusivePeers(extras, catalog, opt, passengerId, action.duffelExtras);
    const already = Array.isArray(next.supplementPaxSelections?.[opt.supplementKey])
      ? next.supplementPaxSelections[opt.supplementKey].includes(passengerId)
      : false;
    if (already) return next;
    return applyLdsSupplementToggle(next, opt.supplementKey, passengerId, true);
  }
  if (opt.kind === "duffel") {
    if (passengerId === "pax-unassigned") return extras;
    const list = Array.isArray(action.duffelExtras) ? action.duffelExtras : [];
    const extra =
      list.find((item) => String(item?.serviceId) === String(opt.serviceId || opt.extraId)) || {
        serviceId: opt.serviceId || opt.extraId,
        maximumQuantity: opt.maximumQuantity ?? 1,
      };
    const base = selected
      ? dropExclusivePeers(extras, catalog, opt, passengerId, list)
      : extras;
    return applyDuffelServiceToggle(
      base,
      extra,
      passengerId,
      selected,
      opt.directionId || null
    );
  }
  return extras;
}

/**
 * Sticky breakdown from catalog selections and already-formatted price labels.
 * extrasTotalLabel is the existing pricing total, not a sum of the lines.
 */
export function buildLiveBaggageBreakdown(catalog, extrasTotalLabel = null) {
  const optionsById = new Map((catalog?.baggageOptions || []).map((opt) => [opt.id, opt]));
  const groups = [];
  const dirs =
    catalog?.directionMode === "whole-trip"
      ? [
          {
            id: "whole",
            label: catalog?.directions?.[0]?.label || "כל ההזמנה",
            routeLabel: catalog?.directions?.[0]?.routeLabel || "",
          },
        ]
      : catalog?.directions || [];
  for (const pax of catalog?.passengers || []) {
    const lines = [];
    for (const dir of dirs) {
      const ids = directionSelectionIds(catalog?.selections?.[pax.id]?.[dir.id]);
      const route = String(dir.routeLabel || "").trim();
      const head = [dir.label, route].filter(Boolean).join(" ");
      for (const optId of ids) {
        const opt = optionsById.get(optId);
        if (!opt || opt.state === "included" || opt.state === "unavailable") continue;
        lines.push({
          label: `${head} / ${opt.name}`,
          amountLabel: opt.priceLabel || "",
        });
      }
    }
    if (lines.length) {
      groups.push({
        id: pax.id,
        passengerName: pax.name,
        lines,
      });
    }
  }
  if (!groups.length) return null;
  if (extrasTotalLabel) {
    groups.push({
      id: "baggage-total",
      passengerName: "",
      lines: [{ label: "סה״כ כבודה", amountLabel: extrasTotalLabel }],
    });
  }
  return {
    defaultExpanded: true,
    expandLabel: "פירוט לפי נוסע",
    collapseLabel: "הסתר פירוט",
    groups,
  };
}

export function withLiveBaggageBreakdown(offer, breakdown) {
  if (!offer || !breakdown?.groups?.length) return offer;
  const summary = offer.bookingSummary || {};
  const baggage = summary.baggage || {
    title: "כבודה",
    items: [],
    extraLine: null,
    breakdown: null,
  };
  return {
    ...offer,
    bookingSummary: {
      ...summary,
      baggage: {
        ...baggage,
        title: baggage.title || "כבודה",
        breakdown,
      },
    },
  };
}

function conditionRowsFromOffer(conditions) {
  if (!conditions || typeof conditions !== "object") {
    return [{ label: "תנאים", value: "לא זמין" }];
  }
  if (Array.isArray(conditions)) {
    const rows = conditions
      .map((row) => ({
        label: row?.label || row?.title || "תנאים",
        value: row?.value || row?.text || "",
      }))
      .filter((row) => row.value);
    return rows.length ? rows : [{ label: "תנאים", value: "לא זמין" }];
  }
  const pairs = [
    ["שינויים", conditions.changes],
    ["ביטול", conditions.cancellation],
    ["החזר", conditions.refund],
    ["דדליין לכרטוס", conditions.ticketingDeadline],
  ].filter(([, value]) => value);
  return pairs.length
    ? pairs.map(([label, value]) => ({ label, value: String(value) }))
    : [{ label: "תנאים", value: "לא זמין" }];
}

function legFromOffer(offer, key, directionLabel) {
  const leg = offer?.[key];
  if (!leg) return null;
  const from = leg.depIata || leg.origin || "";
  const to = leg.arrIata || leg.destination || "";
  const dep = leg.depTime || "";
  const arr = leg.arrTime || "";
  return {
    id: key,
    directionLabel,
    timeRouteLabel: [dep, from, "→", arr, to].filter(Boolean).join(" "),
    metaLabel: [leg.stopsLabel, leg.flightNumber, leg.date].filter(Boolean).join(" · "),
  };
}

export function buildLiveReviewViewModel({ offer, passengers, extrasCatalog }) {
  const contact = bookingContactFromPassengers(passengers);
  const items = (Array.isArray(passengers) ? passengers : []).map((p, index) => {
    const name =
      [p.firstName, p.lastName].filter((part) => String(part || "").trim()).join(" ") ||
      `נוסע ${index + 1}`;
    const passport = String(p.passportNumber || "").trim();
    return {
      id: passengerSlotId(index),
      name,
      metaLabel: [p.dateOfBirth, p.nationality].filter(Boolean).join(" · "),
      statusLabel: name.trim() && p.firstName && p.lastName ? "פרטים הוזנו" : "חסר מידע",
      statusTone: p.firstName && p.lastName ? "complete" : "incomplete",
      passportSummary: passport ? `דרכון · ${passport}` : "",
    };
  });

  const bagPassengers = (extrasCatalog?.passengers || []).map((pax) => {
    const sel = extrasCatalog.selections?.[pax.id] || {};
    const segments = [];
    for (const dir of extrasCatalog.directions || []) {
      const ids = directionSelectionIds(sel[dir.id]);
      for (const optId of ids) {
        const opt = (extrasCatalog.baggageOptions || []).find((row) => row.id === optId);
        if (!opt) continue;
        segments.push({
          id: `${pax.id}-${dir.id}-${opt.id}`,
          routeLabel: dir.label,
          itemLabel: opt.name,
          valueLabel: opt.priceLabel || null,
          tone: opt.state === "unavailable" ? "muted" : opt.priceLabel ? "paid" : "included",
        });
      }
    }
    if (segments.length === 0) {
      const fallback = (extrasCatalog.baggageOptions || [])[0];
      segments.push({
        id: `${pax.id}-empty`,
        routeLabel: null,
        itemLabel: fallback?.name || "פרטי כבודה לא זמינים",
        valueLabel: null,
        tone: "muted",
      });
    }
    return { id: pax.id, name: pax.name, segments };
  });

  const conditionRows = conditionRowsFromOffer(offer?.conditions);

  const legs = [
    legFromOffer(offer, "outbound", "הלוך"),
    legFromOffer(offer, "inbound", "חזור"),
  ].filter(Boolean);

  return {
    title: "סיכום והזמנה",
    subtitle: "בדקו את הפרטים לפני המשך לתשלום.",
    flight: {
      title: "הטיסה",
      airlineLabel: offer?.airline?.name || "—",
      fareFamily: offer?.fare?.family || "—",
      legs,
      editLabel: "עריכה",
      editTarget: "details",
    },
    passengers: {
      title: "נוסעים",
      editLabel: "עריכה",
      editTarget: "passengers",
      items,
    },
    baggage: {
      title: "כבודה ותוספות",
      editLabel: "עריכה",
      editTarget: "extras",
      passengers: bagPassengers,
    },
    contact: {
      title: "פרטי איש קשר",
      editLabel: "עריכה",
      editTarget: "passengers",
      name: contact.name || "—",
      phone: contact.phone || "—",
      email: contact.email || "—",
    },
    fareConditions: {
      title: "תנאי מחיר",
      rows: conditionRows.length ? conditionRows : [{ label: "תנאים", value: "לא זמין" }],
      fullTermsLabel: "תנאים מלאים",
      fullTerms: "",
    },
    confirmation: {
      title: "אישור",
      note: "",
      items: [{ id: "details-checked", label: "בדקתי את פרטי ההזמנה" }],
    },
    cta: {
      continueLabel: "המשך לתשלום",
      backLabel: "חזרה לתוספות",
      requireConfirmation: true,
    },
    bookingSummary: offer?.bookingSummary || null,
  };
}

export function buildLivePaymentViewModel({
  offer,
  holdEligible = false,
  paymentRequiredBy = null,
  status = null,
}) {
  const amountLabel = offer?.price?.totalLabel || "—";
  return {
    title: "תשלום",
    payable: { caption: "לתשלום עכשיו", amountLabel },
    methodsTitle: "בחר אמצעי תשלום",
    methods: [
      { id: "card", label: "כרטיס אשראי" },
      { id: "bank", label: "העברה בנקאית" },
    ],
    selectedActionId: "bank",
    status,
    cardMode: "hosted",
    holdEligible: Boolean(holdEligible),
    hostedCard: {
      title: "כרטיס אשראי",
      amountCaption: "סכום לתשלום",
      amountLabel,
      secureNote: "התשלום יתבצע במסלול התשלום הקיים",
      ctaLabel: "המשך",
    },
    split: null,
    bankDetails: {
      title: "העברה בנקאית",
      amountCaption: "סכום להעברה",
      amountLabel,
      accountTitle: "פרטי חשבון",
      lines: [],
      nextStepTitle: "המשך התהליך",
      nextStepNote: "ההמשך משתמש בהעברה הבנקאית הקיימת. לא נוצר חיוב חדש מהמסך.",
      ctaLabel: "המשך להעברה בנקאית",
    },
    moreOptions: holdEligible
      ? {
          title: "אפשרויות הזמנה נוספות",
          hold: {
            id: "hold",
            label: "שמירת הזמנה ללא כרטוס",
            availableUntilLabel: paymentRequiredBy ? String(paymentRequiredBy) : "",
            priceGuaranteedLabel: "האם המחיר מובטח",
            priceGuaranteedValue: "לא זמין",
            afterExpiryLabel: "לאחר פקיעת השמירה",
            afterExpiryValue: "לא זמין",
            paymentLaterLabel: "תשלום בהמשך",
            paymentLaterValue: "לפי סטטוס ההזמנה שנשמר",
            ctaLabel: "שמור הזמנה",
          },
        }
      : null,
    statusCopy: {
      processing: { title: "מעבד", body: "" },
      success: { title: "התשלום עודכן", body: "" },
      failed: {
        title: "התשלום לא הושלם",
        body: "",
        retryLabel: "נסה שוב",
        otherMethodLabel: "בחר אמצעי תשלום אחר",
      },
    },
    bookingSummary: offer?.bookingSummary || null,
  };
}

function firstPersistedRef(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text) continue;
    if (text.startsWith("MOCK")) continue;
    return text;
  }
  return null;
}

export function buildLiveConfirmationViewModel({
  holdResult = null,
  orderRow = null,
  passengers = [],
  offer = null,
  bookingStatus = null,
}) {
  const reference = firstPersistedRef(
    holdResult?.bookingReference,
    holdResult?.hrBookingNumber,
    holdResult?.providerOrderId,
    orderRow?.pnr,
    orderRow?.orderNumber,
    orderRow?.bookingReference,
    orderRow?.id
  );
  const held = Boolean(holdResult);
  const confirmed = bookingStatus === "confirmed" && !held;
  const names = (Array.isArray(passengers) ? passengers : []).map((p, index) => ({
    id: passengerSlotId(index),
    name:
      [p.firstName, p.lastName].filter((part) => String(part || "").trim()).join(" ") ||
      `נוסע ${index + 1}`,
  }));

  return {
    title: "אישור הזמנה",
    tone: held ? "amber" : confirmed ? "blue" : "muted",
    header: {
      title: held ? "ההזמנה נשמרה" : confirmed ? "ההזמנה נקלטה" : "אין אישור שמור",
      reference,
      badgeLabel: held ? "Hold" : confirmed ? "Booked" : "",
      badgeTone: held ? "amber" : "blue",
      copyLabel: reference ? "העתק" : "",
    },
    timeline: [
      {
        label: "הזמנה",
        value: held || confirmed ? "נשמר" : "ממתין",
        tone: held || confirmed ? "blue" : "muted",
      },
    ],
    next: null,
    hold: held
      ? {
          expiresLabel: "מועד תשלום",
          expiresValue: holdResult?.paymentRequiredBy
            ? String(holdResult.paymentRequiredBy)
            : "לא זמין",
          priceGuaranteeLabel: "האם המחיר מובטח",
          priceGuaranteeValue: holdResult?.priceGuaranteeExpiresAt
            ? String(holdResult.priceGuaranteeExpiresAt)
            : "לא זמין",
          amountDueLabel: "לתשלום",
          amountDueValue:
            holdResult?.customerTotal?.amount != null
              ? formatFlightReviewDisplayMoney(
                  holdResult.customerTotal.amount,
                  holdResult.customerTotal.currency || "ILS"
                )
              : offer?.price?.totalLabel || "—",
        }
      : null,
    bankPending: null,
    failed: null,
    tickets: null,
    flight: {
      airlineLabel: offer?.airline?.name || "—",
      fareFamily: offer?.fare?.family || "—",
      routeLabel: [offer?.outbound?.depIata, offer?.outbound?.arrIata].filter(Boolean).join(" → "),
      datesLabel: offer?.outbound?.date || "",
      passengersLabel: names.length ? `${names.length} נוסעים` : "",
      totalLabel: offer?.price?.totalLabel || "",
    },
    passengers: names,
    payment: null,
    primaryAction: held
      ? { id: "pay", label: "לתשלום עכשיו" }
      : null,
    secondaryActions: [{ id: "home", label: "חזרה למערכת" }],
  };
}

export function contextDateToIso(value) {
  const raw = String(value || "").trim();
  const dmy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return "";
}

export function isoToContextDate(iso, sample) {
  const raw = String(iso || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return raw;
  const dmy = `${m[3]}/${m[2]}/${m[1]}`;
  const current = String(sample || "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(current)) return raw;
  return dmy;
}

function airportCodeOf(location) {
  if (!location || typeof location !== "object") return "";
  return String(location.AIRPORTCODE || location.iata || location.code || "")
    .trim()
    .toUpperCase();
}

/**
 * Writes the preview search form through the existing search setters, then
 * calls submitSearch. Room composition is left untouched.
 */
export function submitFlightSearchFromScreen({
  form,
  current,
  findAirport,
  setOrigin,
  setLocation,
  setFlightTripType,
  setCalendarDateArrival,
  setCalendarDateLeave,
  submitSearch,
}) {
  const origin = typeof findAirport === "function" ? findAirport(form?.origin) : null;
  const location = typeof findAirport === "function" ? findAirport(form?.destination) : null;
  if (!origin || !location) {
    return { ok: false, reason: "airport_not_in_catalog" };
  }
  const trip = form?.tripType === "oneway" ? "OneWay" : "RoundTrip";
  if (typeof setFlightTripType === "function" && current?.flightTripType !== trip) {
    setFlightTripType(trip);
  }
  if (typeof setOrigin === "function" && airportCodeOf(current?.origin) !== airportCodeOf(origin)) {
    setOrigin(origin);
  }
  if (
    typeof setLocation === "function" &&
    airportCodeOf(current?.location) !== airportCodeOf(location)
  ) {
    setLocation(location);
  }
  const depart = isoToContextDate(form?.departDate, current?.calendarDateArrival);
  const ret = trip === "OneWay" ? "" : isoToContextDate(form?.returnDate, current?.calendarDateLeave);
  if (typeof setCalendarDateArrival === "function" && depart !== current?.calendarDateArrival) {
    setCalendarDateArrival(depart);
  }
  if (typeof setCalendarDateLeave === "function" && ret !== (current?.calendarDateLeave || "")) {
    setCalendarDateLeave(ret);
  }
  if (typeof submitSearch === "function") submitSearch({ source: "button" });
  return {
    ok: true,
    payload: {
      origin,
      location,
      flightTripType: trip,
      calendarDateArrival: depart,
      calendarDateLeave: ret,
      roomInfo: current?.roomInfo ?? null,
    },
  };
}
